import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { History, Heart, Award, Mail } from 'lucide-react';
import SEO from '../components/SEO';

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-cyan-50">
      <SEO
        title="About Us"
        description="Learn about J.B.F. Antiques & Gallery at Doncaster Corn Exchange. We specialise in quality antiques, from Victorian furniture to Art Deco collectibles."
        path="/about"
      />
      {/* Header Section */}
      <section className="relative py-20 bg-teal-700 text-white">
        <div className="container mx-auto px-4">
          <h1 className="text-5xl font-serif font-bold mb-6 text-center">
            About J.B.F. Antiques & Gallery
          </h1>
          <p className="text-xl text-teal-100 max-w-3xl mx-auto text-center">
            A passion for preserving history and sharing timeless treasures with collectors and enthusiasts
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <video
                src="/videos/about-video.mp4"
                autoPlay
                loop
                muted
                playsInline
                className="rounded-lg shadow-xl w-full h-[500px] object-cover"
              />
            </div>
            <div>
              <h2 className="text-4xl font-serif font-bold text-teal-800 mb-6">
                Our Story
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Welcome to J.B.F. Antiques & Gallery, where history comes alive through carefully curated treasures from bygone eras. 
                  Located in the heart of the historic Doncaster Corn Exchange, our gallery has become a destination for antique enthusiasts, 
                  collectors, and those seeking unique pieces with character and heritage.
                </p>
                <p>
                  Our passion for antiques stems from a deep appreciation of craftsmanship, design, and the stories each piece carries. 
                  Every item in our collection has been carefully selected for its quality, authenticity, and historical significance.
                </p>
                <p>
                  From elegant Victorian furniture to exquisite Art Deco accessories, from Georgian silverware to rare collectibles, 
                  we pride ourselves on offering a diverse range of authenticated antiques that span multiple eras and styles.
                </p>
                <p className="text-teal-700 font-medium">
                  We invite you to visit our gallery and experience the beauty of these timeless treasures firsthand.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-serif font-bold text-teal-800 mb-12 text-center">
            What We Stand For
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="border-teal-200 hover:shadow-lg transition-shadow">
              <CardContent className="pt-8 text-center">
                <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <History className="text-teal-700" size={40} />
                </div>
                <h3 className="text-2xl font-serif font-bold text-teal-800 mb-4">
                  Authenticity
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Every piece in our collection is carefully authenticated and comes with detailed provenance. 
                  We guarantee the genuineness of all our antiques.
                </p>
              </CardContent>
            </Card>

            <Card className="border-teal-200 hover:shadow-lg transition-shadow">
              <CardContent className="pt-8 text-center">
                <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Heart className="text-teal-700" size={40} />
                </div>
                <h3 className="text-2xl font-serif font-bold text-teal-800 mb-4">
                  Passion
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Our love for antiques drives everything we do. We're not just dealers; 
                  we're enthusiasts who appreciate the artistry and history of each piece.
                </p>
              </CardContent>
            </Card>

            <Card className="border-teal-200 hover:shadow-lg transition-shadow">
              <CardContent className="pt-8 text-center">
                <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Award className="text-teal-700" size={40} />
                </div>
                <h3 className="text-2xl font-serif font-bold text-teal-800 mb-4">
                  Expertise
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  With years of experience in the antiques trade, we offer expert knowledge and guidance 
                  to help you find the perfect piece for your collection.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section className="py-16 bg-gradient-to-b from-white to-cyan-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-serif font-bold text-teal-800 mb-6">
              Visit Us at Doncaster Corn Exchange
            </h2>
            <p className="text-gray-700 leading-relaxed mb-8">
              Our gallery is situated in the beautiful and historic Doncaster Corn Exchange, 
              a landmark building that provides the perfect setting for our collection of antiques. 
              The venue itself is steeped in history, adding to the unique atmosphere of your visit.
            </p>
            <div className="bg-white rounded-lg shadow-lg p-8 border border-teal-200">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
                <div>
                  <h3 className="font-bold text-teal-800 mb-2">Address</h3>
                  <p className="text-gray-600">
                    Doncaster Corn Exchange<br />
                    Doncaster DN1 1NJ<br />
                    United Kingdom
                  </p>
                </div>
                <div>
                  <h3 className="font-bold text-teal-800 mb-2">Opening Hours</h3>
                  <p className="text-gray-600">
                    Tuesday - Saturday<br />
                    9:00 AM - 4:00 PM
                  </p>
                </div>
                <div>
                  <h3 className="font-bold text-teal-800 mb-2">Contact</h3>
                  <a 
                    href="mailto:Justineledodman@gmail.com" 
                    className="text-teal-600 hover:text-teal-700 flex items-center gap-2"
                  >
                    <Mail size={16} />
                    Justineledodman@gmail.com
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
